/**
 * Main App Component
 * Mobile-first chatbot with Alibaba ChatUI and JSON-driven flows
 */

import { useState, useEffect, useCallback } from "react";
import Chat, { Bubble, useMessages } from "@chatui/core";
import "@chatui/core/dist/index.css";
import "./styles/theme.css";
import BubbleButton from "./components/BubbleButton";
import ChatFlowEngine from "./utils/chatFlowEngine";
import type { ChatFlow } from "./types/flow";
import {
  getPixelIdsFromQuery,
  getCtaUrlFromQuery,
  getEffectiveCtaUrl,
  initFacebookPixels,
  initGTM,
  trackCtaClick,
} from "./utils/marketing";

// Import the job hiring flow
import jobHiringFlow from "./flows/job-hiring.json";
import type { QuickReplyOption } from "./types/flow";

function App() {
  const { messages, appendMsg, setTyping, deleteMsg } = useMessages([]);
  const [flowEngine, setFlowEngine] = useState<ChatFlowEngine | null>(null);
  const [, setCurrentOptions] = useState<QuickReplyOption[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [streamingText, setStreamingText] = useState<string>("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [dynamicCtaUrl, setDynamicCtaUrl] = useState<string | null>(null);

  // Helper function to stream text character by character
  const streamText = useCallback(
    async (text: string) => {
      const words = text.split(" ");
      let currentText = "";

      setIsStreaming(true);
      setStreamingText("");

      // Stream the text word by word
      for (let i = 0; i < words.length; i++) {
        currentText += (i > 0 ? " " : "") + words[i];
        setStreamingText(currentText);

        // Delay between words (adjust speed here: lower = faster)
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      // Once streaming is complete, add the full message
      setIsStreaming(false);
      appendMsg({
        type: "text",
        content: { text: text },
        position: "left",
      });
      setStreamingText("");
    },
    [appendMsg],
  );

  // Initialize GTM + FB Pixel + CTA URL and the chat flow
  useEffect(() => {
    if (isInitialized) return;

    let cancelled = false;
    let initTimer: number | undefined;

    const initializeFlow = async () => {
      try {
        // Initialize GTM
        initGTM();
        console.log("[marketing] initGTM called");

        // Initialize Facebook Pixels from query and fire PageView
        const pxIds = getPixelIdsFromQuery();
        if (pxIds.length > 0) {
          initFacebookPixels(pxIds);
          console.log("[marketing] initFacebookPixels called with:", pxIds);
        } else {
          console.log("[marketing] no pixel_id found in query");
        }

        // Load CTA URL from query (validated later against whitelist per render)
        const qCta = getCtaUrlFromQuery();
        setDynamicCtaUrl(qCta);
        console.log("[marketing] dynamic CTA URL from query:", qCta);

        const validation = ChatFlowEngine.validateFlow(jobHiringFlow);
        if (!validation.valid) {
          console.error("Invalid flow:", validation.errors);
          return;
        }

        const engine = new ChatFlowEngine(jobHiringFlow as ChatFlow);
        setFlowEngine(engine);

        const initialMessage = engine.getInitialMessage();

        // Small delay before showing first message; ensure cleanup prevents duplicates
        initTimer = window.setTimeout(async () => {
          if (cancelled) return;

          await streamText(initialMessage.content.text);

          if (initialMessage.content.options) {
            setCurrentOptions(initialMessage.content.options);
            appendMsg({
              type: "quick-replies",
              content: { options: initialMessage.content.options },
              position: "left",
            });
          }
          // Mark as initialized only after appending to avoid StrictMode cleanup race
          setIsInitialized(true);
        }, 500);
        // Note: do not set initialized here; see above
      } catch (error) {
        console.error("Failed to initialize flow:", error);
      }
    };

    initializeFlow();

    return () => {
      cancelled = true;
      if (initTimer) {
        window.clearTimeout(initTimer);
      }
    };
  }, [isInitialized, appendMsg, streamText]);

  // Handle user selection of a quick reply option
  const handleQuickReplyClick = useCallback(
    (optionValue: string, optionLabel: string) => {
      if (!flowEngine) return;

      // Remove the quick replies message from chat
      const quickReplyMsgIndex = messages.findIndex(
        (msg) => msg.type === "quick-replies",
      );
      if (quickReplyMsgIndex !== -1) {
        deleteMsg(messages[quickReplyMsgIndex]._id);
      }

      // Clear current options
      setCurrentOptions([]);

      // Add user's selection message
      appendMsg({
        type: "text",
        content: { text: optionLabel },
        position: "right",
      });

      // Show typing indicator
      setTyping(true);

      // Process the selection and get next messages
      setTimeout(async () => {
        try {
          const messages = flowEngine.processUserSelection(optionValue);

          // Hide typing indicator
          setTyping(false);

          // Check if this was a CTA button (only returns user message)
          if (messages.length === 1) {
            // No bot response needed for CTA buttons with URLs
            // The BubbleButton component handles the URL navigation
            return;
          }

          const [, botMsg] = messages;

          // Add bot response with streaming
          await streamText(botMsg.content.text);

          // Show new options if available
          if (botMsg.content.options) {
            setCurrentOptions(botMsg.content.options);
            // Add quick replies as a message in the chat flow
            appendMsg({
              type: "quick-replies",
              content: { options: botMsg.content.options },
              position: "left",
            });
          } else {
            // Check if flow is completed
            if (flowEngine.isCompleted()) {
              // Show restart option after a delay
              setTimeout(() => {
                const restartOptions = [
                  {
                    label: "🔄 Mulakan Semula",
                    value: "restart",
                    nextNode: "welcome",
                  },
                ];
                setCurrentOptions(restartOptions);
                appendMsg({
                  type: "quick-replies",
                  content: { options: restartOptions },
                  position: "left",
                });
              }, 2000);
            }
          }
        } catch (error) {
          console.error("Error processing selection:", error);
          setTyping(false);
        }
      }, 800);
    },
    [flowEngine, appendMsg, setTyping, messages, deleteMsg, streamText],
  );

  // Handle restart
  const handleRestart = useCallback(() => {
    if (!flowEngine) return;

    // Remove the quick replies message from chat
    const quickReplyMsgIndex = messages.findIndex(
      (msg) => msg.type === "quick-replies",
    );
    if (quickReplyMsgIndex !== -1) {
      deleteMsg(messages[quickReplyMsgIndex]._id);
    }

    // Clear messages
    setCurrentOptions([]);

    // Reset flow engine
    flowEngine.reset();

    // Get initial message again
    const initialMessage = flowEngine.getInitialMessage();

    setTimeout(async () => {
      await streamText(initialMessage.content.text);

      if (initialMessage.content.options) {
        setCurrentOptions(initialMessage.content.options);
        // Add quick replies as a message in the chat flow
        appendMsg({
          type: "quick-replies",
          content: { options: initialMessage.content.options },
          position: "left",
        });
      }
    }, 500);
  }, [flowEngine, appendMsg, messages, deleteMsg, streamText]);

  // Render message content
  const renderMessageContent = (rawMsg: unknown) => {
    if (!rawMsg || typeof rawMsg !== "object") return null;

    const msg = rawMsg as {
      type?: unknown;
      content?: {
        text?: string;
        options?: QuickReplyOption[];
      };
    };

    // Check if this is a quick reply message type
    if (msg.type === "quick-replies") {
      const options = msg.content?.options ?? [];
      return (
        <div className="quick-replies-inline">
          {options.map((option, index) => {
            const optionValue = option.value ?? option.nextNode ?? option.label;
            const isRestart = optionValue === "restart";
            const effectiveUrl = option.isCTA
              ? getEffectiveCtaUrl(dynamicCtaUrl, option.url ?? null)
              : undefined;
            if (option.isCTA) {
              console.log("[marketing] quick-reply CTA render:", {
                label: option.label,
                originalUrl: option.url,
                dynamicCtaUrl,
                effectiveUrl,
              });
            }

            return (
              <BubbleButton
                key={`${optionValue}-${index}`}
                label={option.label}
                icon={option.icon}
                url={effectiveUrl || undefined}
                isCTA={option.isCTA}
                // Pass original URL for tracking even if effectiveUrl is null
                originalUrl={option.url}
                onClick={() => {
                  if (isRestart) {
                    handleRestart();
                  } else {
                    handleQuickReplyClick(optionValue, option.label);
                  }
                }}
              />
            );
          })}
        </div>
      );
    }

    return <Bubble content={msg.content?.text ?? ""} />;
  };

  // Render CTA button at the bottom
  const renderCTAButton = () => {
    return (
      <div className="cta-container">
        <button
          className="cta-button cta_click"
          onClick={() => {
            try {
              window.sendLpEvent?.("click");
            } catch (err) {
              void err;
            }
            // Tracking: requested FB events + GTM custom event
            trackCtaClick();
            const effective = getEffectiveCtaUrl(dynamicCtaUrl, undefined);
            if (effective) {
              // Minimal delay (100ms) to allow pixel events to fire while keeping UX snappy
              setTimeout(() => {
                console.log("[marketing] opening bottom CTA URL:", effective);
                window.open(effective, "_blank", "noopener,noreferrer");
              }, 100);
            } else {
              console.log("[marketing] bottom CTA has no allowed URL");
            }
          }}
        >
          Mohon sekarang
        </button>
      </div>
    );
  };

  // Create a combined messages array with streaming message
  const displayMessages = isStreaming
    ? [
        ...messages,
        {
          _id: "streaming",
          type: "text",
          content: { text: streamingText },
          position: "left" as const,
        },
      ]
    : messages;

  const whatsappCtaUrl = getEffectiveCtaUrl(dynamicCtaUrl, null);

  return (
    <div className="app-container">
      <div className="custom-navbar">
        <img
          src="/lacoste-favicon.jpeg"
          alt="Lacoste"
          className="navbar-logo"
        />
        <div className="navbar-spacer" />
        <button
          type="button"
          className="whatsapp-cta cta_click"
          onClick={() =>
            (() => {
              try {
                window.sendLpEvent?.("click");
              } catch (err) {
                void err;
              }
              trackCtaClick();
              if (!whatsappCtaUrl) return;
              window.open(whatsappCtaUrl, "_blank", "noopener,noreferrer");
            })()
          }
          disabled={!whatsappCtaUrl}
          aria-label="WhatsApp"
          title={whatsappCtaUrl ? "WhatsApp" : "WhatsApp (tiada pautan)"}
        >
          <span className="whatsapp-cta__icon" aria-hidden="true">
            <svg
              className="whatsapp-cta__iconSvg"
              viewBox="0 0 24 24"
              role="img"
              focusable="false"
              aria-hidden="true"
            >
              <path d="M12.04 2C6.55 2 2.08 6.47 2.08 11.97c0 1.78.47 3.52 1.37 5.05L2 22l5.12-1.35c1.48.81 3.15 1.24 4.87 1.24h.01c5.5 0 9.97-4.47 9.97-9.96C21.97 6.47 17.54 2 12.04 2Zm5.79 14.37c-.24.67-1.2 1.25-1.93 1.41-.5.11-1.15.19-3.33-.71-2.79-1.16-4.6-3.99-4.74-4.17-.14-.18-1.13-1.51-1.13-2.88 0-1.37.72-2.05.97-2.33.25-.28.55-.35.73-.35.18 0 .37 0 .53.01.17.01.4-.06.63.48.24.58.79 2 .86 2.14.07.14.12.31.02.49-.09.18-.14.31-.28.48-.14.17-.3.38-.42.51-.14.14-.28.29-.12.56.16.27.71 1.17 1.52 1.9 1.05.95 1.93 1.24 2.21 1.38.28.14.44.12.61-.07.17-.2.7-.82.89-1.1.18-.28.37-.23.63-.14.26.09 1.64.77 1.92.91.28.14.47.21.54.33.07.12.07.69-.17 1.36Z" />
            </svg>
          </span>
          <span className="whatsapp-cta__label">WhatsApp</span>
        </button>
      </div>
      <Chat
        navbar={{
          title: "",
        }}
        messages={displayMessages}
        renderMessageContent={renderMessageContent}
        placeholder="Klik pada pilihan di atas"
        onSend={() => {}} // No-op, input is disabled
      />
      {renderCTAButton()}
    </div>
  );
}

export default App;
